import { useEffect, useState } from 'react';
import {
  collection, query, onSnapshot, addDoc, serverTimestamp, orderBy,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import { totalTravellers } from '../../utils/groupSize';

const FOOD_TYPES = [
  { value: 'sandwich', label: 'Sandwiches' },
  { value: 'tgb', label: 'TGB' },
  { value: 'belaCafe', label: 'Bela Café' },
];

function emptyItem() {
  return { name: '', quantity: 1, notes: '' };
}

export default function FoodOrders({ requestId, request }) {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  // Form state
  const [type, setType] = useState('sandwich');
  const [date, setDate] = useState('');
  const [groupSize, setGroupSize] = useState(String(totalTravellers(request) || ''));
  const [items, setItems] = useState([emptyItem()]);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const q = query(
      collection(db, 'requests', requestId, 'foodOrders'),
      orderBy('submittedAt', 'desc')
    );
    const unsub = onSnapshot(q, (snap) => {
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
    return unsub;
  }, [requestId]);

  function addItem() {
    setItems((prev) => [...prev, emptyItem()]);
  }

  function removeItem(i) {
    setItems((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateItem(i, key, value) {
    setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [key]: value } : it));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    const validItems = items.filter((it) => it.name.trim());
    if (validItems.length === 0) {
      setError('Add at least one item.');
      return;
    }
    setSaving(true);
    try {
      await addDoc(collection(db, 'requests', requestId, 'foodOrders'), {
        type,
        date: date.trim(),
        groupSize: Number(groupSize) || 0,
        items: validItems.map((it) => ({
          name: it.name.trim(),
          quantity: Number(it.quantity) || 1,
          notes: it.notes.trim(),
        })),
        notes: notes.trim(),
        status: 'pending',
        submittedBy: currentUser?.uid || null,
        submittedAt: serverTimestamp(),
      });
      setShowForm(false);
      setDate('');
      setGroupSize(String(totalTravellers(request) || ''));
      setItems([emptyItem()]);
      setNotes('');
      setType('sandwich');
    } catch (err) {
      console.error(err);
      setError('Failed to submit. Please try again.');
    } finally {
      setSaving(false);
    }
  }

  const typeLabel = (t) => FOOD_TYPES.find((f) => f.value === t)?.label || t;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <p className="text-[13px] text-[#6B7280]">Submit food and meal orders for your suppliers.</p>
        {!showForm && (
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-1.5 bg-pb hover:bg-navy text-white text-[13px] font-bold px-[14px] py-[8px] rounded-[9px] transition shadow-sm"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New order
          </button>
        )}
      </div>

      {showForm && (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
          <h3 className="text-[14px] font-bold text-[#111827] mb-4">New Food Order</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Type + Date + Group size */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Food type</label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb bg-white"
                >
                  {FOOD_TYPES.map((f) => (
                    <option key={f.value} value={f.value}>{f.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Date / Meal</label>
                <input
                  type="text"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  placeholder="e.g. Day 2 Lunch or 14 Jun"
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
              <div>
                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Group size</label>
                <input
                  type="number"
                  min="1"
                  value={groupSize}
                  onChange={(e) => setGroupSize(e.target.value)}
                  className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                />
              </div>
            </div>

            {/* Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px]">Items</label>
                <button type="button" onClick={addItem}
                  className="text-[12px] font-bold text-pb hover:text-navy transition">
                  + Add item
                </button>
              </div>
              <div className="space-y-2">
                {items.map((item, i) => (
                  <div key={i} className="flex gap-2 items-start">
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.name}
                        onChange={(e) => updateItem(i, 'name', e.target.value)}
                        placeholder="Item name (e.g. Ham & Cheese Sandwich)"
                        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                      />
                    </div>
                    <div className="w-16 flex-shrink-0">
                      <input
                        type="number"
                        min="1"
                        value={item.quantity}
                        onChange={(e) => updateItem(i, 'quantity', e.target.value)}
                        placeholder="Qty"
                        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb text-center"
                      />
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.notes}
                        onChange={(e) => updateItem(i, 'notes', e.target.value)}
                        placeholder="Notes (optional)"
                        className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                      />
                    </div>
                    {items.length > 1 && (
                      <button type="button" onClick={() => removeItem(i)}
                        className="text-[#9CA3AF] hover:text-dr transition mt-2 flex-shrink-0">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-[11px] font-extrabold text-[#9CA3AF] uppercase tracking-[0.7px] mb-1">Additional notes</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                placeholder="Any dietary requirements, allergies, or delivery notes…"
                className="w-full border border-[#E5E7EB] rounded-[8px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb resize-none"
              />
            </div>

            {error && <p className="text-[12px] text-dr">{error}</p>}

            <div className="flex gap-2">
              <button type="submit" disabled={saving}
                className="px-[16px] py-[9px] text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition disabled:opacity-60">
                {saving ? 'Submitting…' : 'Submit order'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setError(''); }}
                className="px-4 py-2 text-[13px] text-[#6B7280] hover:text-[#374151] transition">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Existing orders */}
      {loading ? (
        <p className="text-[13px] text-[#9CA3AF]">Loading…</p>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-8 text-center">
          <p className="text-[13px] text-[#9CA3AF]">No food orders submitted yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-[12px] border border-[#E5E7EB] p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold bg-[#F3F4F6] text-[#374151] px-[9px] py-[3px] rounded-[12px]">
                    {typeLabel(order.type)}
                  </span>
                  {order.date && <span className="text-[12px] text-[#6B7280]">{order.date}</span>}
                </div>
                <span className="text-[12px] text-[#9CA3AF]">Group of {order.groupSize || '—'}</span>
              </div>
              <div className="space-y-1">
                {(order.items || []).map((item, i) => (
                  <div key={i} className="flex items-baseline gap-2">
                    <span className="text-[13px] font-semibold text-[#374151] min-w-[30px]">{item.quantity}×</span>
                    <span className="text-[13px] text-[#111827]">{item.name}</span>
                    {item.notes && <span className="text-[12px] text-[#9CA3AF] italic">({item.notes})</span>}
                  </div>
                ))}
              </div>
              {order.notes && (
                <p className="text-[12px] text-[#9CA3AF] mt-2 italic">{order.notes}</p>
              )}
              {order.submittedAt?.toDate && (
                <p className="text-[11px] text-[#D1D5DB] mt-2">
                  Submitted {order.submittedAt.toDate().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
